import { SEARCH_DEPLOYMENT_ID } from '@integration-layer/data/xGenIds'
import type { AlgoliaProductType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import { stripCloudinaryDimensionsFromProduct } from '@integration-layer/utils/stripCloudinaryDimensions'

/**
 * Parameters for performing a search query with Xgen.
 * @typedef {Object} SearchParams
 */
type SearchParams = {
  /** Search query string */
  q: string
  /** Brand filter */
  brand?: string
  /** Color filter (single or multiple) */
  color?: string | string[]
  /** Micro category filter */
  microCat?: string | string[]
  /** Macro category filter */
  macroCat?: string | string[]
  /** Gender filter */
  gender?: string | string[]
  /** Size filter */
  size?: string | string[]
  /** Age filter */
  age?: string | string[]
  /** Page number for pagination */
  page?: number
}

/**
 * Parameters for fetching recommendations from Xgen.
 * @typedef {Object} RecommendationParams
 */
type RecommendationParams = {
  /** Unique prediction identifier */
  predictionID: string
}

/**
 * Interface for Xgen integration SDK.
 * @interface XgenIntegration
 */
interface XgenIntegration {
  /**
   * Performs a search query with specified options.
   * @param {string} query - Search query string
   * @param {SearchOptions} options - Search configuration options
   * @returns {Promise<any>} Search results
   */
  search: (query: string, options: SearchOptions) => Promise<any>

  /**
   * Retrieves product recommendations based on a prediction ID.
   * @param {string} predictionID - Unique prediction identifier
   * @returns {Promise<any>} Recommended products
   */
  getXgenPrediction: (predictionID: string) => Promise<any>

  /**
   * Sets a context attribute for search filtering.
   * @param {string} attribute - Context attribute name
   * @param {string} value - Context attribute value
   */
  setContext: (attribute: string, value: string) => void

  /** Clears all set context attributes */
  clearContext: () => void
}

/**
 * Configuration options for Xgen search.
 * @interface SearchOptions
 */
interface SearchOptions {
  /** Unique deployment identifier */
  deploymentId: string
  /** Page number for pagination */
  page: number
  /** Context attributes for filtering */
  context: { [key: string]: string | string[] }
  /** Whether to return facet information */
  facets: boolean
}

/**
 * Composable for interacting with Xgen AI-powered search and recommendation services.
 *
 * @description
 * Provides methods to perform AI-enhanced product searches and fetch recommendations.
 *
 * @returns {Object} An object with Xgen integration methods.
 */
/** Module-level shared state for the Xgen SDK (client-only) */
let xgenInstance: XgenIntegration | undefined
let xgenLoadPromise: Promise<void> | undefined

export const useXgen = () => {
  /** Retrieve Xgen API key from runtime configuration */
  const API_KEY = useRuntimeConfig().public.xgen

  /**
   * Dynamically loads the Xgen integration script.
   * Shared across all composable instances — the SDK is loaded only once.
   */
  const checkScript = async () => {
    if (xgenInstance || !import.meta.client) return

    if (!xgenLoadPromise) {
      xgenLoadPromise = (async () => {
        const url = `https://assets.xgen.dev/js/${API_KEY}/sdkIntegration.js`
        try {
          const { integration } = await import(/* @vite-ignore */ url)
          xgenInstance = integration as XgenIntegration
        } catch (error) {
          // Imported here, not at the top: a static import pulls the whole logging
          // package into the entry chunk for a line that runs only on failure.
          //
          // Not awaited, and guarded on the resolved value as well as the
          // promise: throwing while reporting a failure would reject
          // `xgenLoadPromise`, replacing this error with a TypeError about
          // logging and taking the swallowed-failure path with it.
          void import('@datadog/browser-logs')
            .then(logs =>
              logs?.datadogLogs?.logger.info(
                'Failed to load Xgen integration script',
                { sdkError: error }
              )
            )
            .catch(() => undefined)
        }
      })()
    }

    await xgenLoadPromise
  }

  /**
   * Performs an AI-enhanced product search with multiple filtering options.
   *
   * @param {SearchParams} searchParams - Search configuration parameters
   * @returns {Promise<any>} Search results with optional facets
   * @throws {Error} If Xgen integration fails to load
   * @description
   * - Supports multi-dimensional filtering (brand, color, category, etc.)
   * - Uses wildcard (*) for unspecified filters
   * - Includes pagination and facet information
   */
  const search = async ({
    q,
    brand,
    color,
    microCat,
    macroCat,
    gender,
    size,
    age,
    page,
  }: SearchParams) => {
    await checkScript()
    if (!xgenInstance) {
      console.warn('Xgen integration is not loaded, returning empty results.')
      return { products: [], facets: {} }
    }

    const { search } = xgenInstance

    const context = {
      Brand: brand ?? '*',
      ColorDesc: color?.length ? color : '*', // ['Blue', 'Black']
      macroCat: macroCat?.length ? macroCat : '*', // ['tshirt']
      microCat: microCat?.length ? microCat : '*',
      Gender: gender?.length ? gender : '*',
      size: size?.length ? size : '*', // ['XL']
      Age: age?.length ? age : '*',
    }

    return search(q, {
      deploymentId: SEARCH_DEPLOYMENT_ID,
      page: page ?? 0,
      context,
      facets: true,
    })
  }

  /**
   * Fetches product recommendations based on a prediction ID.
   *
   * @param {RecommendationParams} params - Recommendation request parameters
   * @returns {Promise<AlgoliaProductType[]>} Recommended products
   * @throws {Error} If Xgen integration fails to load
   * @description
   * - Retrieves personalized product recommendations
   * - Returns products in Algolia-compatible format
   */
  const recommendations = async ({
    predictionID,
  }: RecommendationParams): Promise<AlgoliaProductType[]> => {
    await checkScript()
    if (!xgenInstance) {
      console.warn(
        'Xgen integration is not loaded, returning empty recommendations.'
      )
      return []
    }

    const { getXgenPrediction } = xgenInstance

    try {
      const products = await getXgenPrediction(predictionID)
      if (!Array.isArray(products)) return []

      // Xgen catalog URLs often include fixed Cloudinary crops (w_*, h_*);
      // strip them so Nuxt Image can apply responsive sizing.
      return products.map(product =>
        stripCloudinaryDimensionsFromProduct(product)
      ) as AlgoliaProductType[]
    } catch (error) {
      console.error('[Xgen] recommendations failed', { predictionID, error })
      return []
    }
  }

  return {
    /** Checks and loads Xgen integration script */
    checkScript,
    /** Performs AI-enhanced product search */
    search,
    /** Fetches personalized product recommendations */
    recommendations,
  }
}
